import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Button, Table } from 'src/common';
import { getPageTitle } from 'src/config/projectConfig';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import type { Column, TableData } from 'src/types/table';
import {
  COMPLIANCE_SETTINGS_FRAMEWORK_OPTIONS,
  fetchFrameworksByOrganization,
  fetchFrameworksByReseller,
  patchOrganizationFrameworks,
  type ComplianceFrameworkOrg,
} from './complianceSettingsApi';
import {
  EDR_COLORS,
  EdrDataCard,
  EdrDataToolbar,
  EdrPageHeader,
  edrMutedStyle,
} from '../edr/shared/edrPrimitives';

const formatNumber = (value: number | undefined | null): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return value.toLocaleString();
};

const frameworksEqual = (left: string[], right: string[]): boolean => {
  if (left.length !== right.length) return false;
  const rightSet = new Set(right);
  return left.every((value) => rightSet.has(value));
};

const getApiErrorMessage = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  if ('message' in error && typeof (error as { message?: unknown }).message === 'string') {
    return (error as { message: string }).message;
  }
  if ('response' in error) {
    return (error as { response?: { data?: { message?: string } } }).response?.data?.message;
  }
  return undefined;
};

type ComplianceMatrixProps = {
  orgId: string;
  isReseller: boolean;
};

const ComplianceMatrix: FC<ComplianceMatrixProps> = ({ orgId, isReseller }) => {
  const { t } = useTranslation('securityComplianceSettings');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [baseline, setBaseline] = useState<Record<string, string[]>>({});
  /** Pending edits keyed by org id — survives pagination/search until Save. */
  const [drafts, setDrafts] = useState<Record<string, string[]>>({});
  const [rows, setRows] = useState<ComplianceFrameworkOrg[]>([]);

  useEffect(() => {
    if (!isReseller) return undefined;
    const handle = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchInput, isReseller]);

  const loadFrameworks = useCallback(async () => {
    setLoading(true);
    try {
      let loaded: ComplianceFrameworkOrg[] = [];
      if (isReseller) {
        loaded = await fetchFrameworksByReseller(orgId);
      } else {
        const single = await fetchFrameworksByOrganization(orgId);
        loaded = single ? [single] : [];
      }

      const nextBaseline: Record<string, string[]> = {};
      loaded.forEach((row) => {
        nextBaseline[row.id] = [...row.frameworks];
      });
      setRows(loaded);
      setBaseline(nextBaseline);
      setDrafts({});
      setPage(0);
    } catch (error: unknown) {
      setRows([]);
      setBaseline({});
      setDrafts({});
      toast.error(getApiErrorMessage(error) ?? (isReseller ? t('loadClientsFailed') : t('loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [orgId, isReseller, t]);

  useEffect(() => {
    if (!orgId) return undefined;
    void loadFrameworks();
    return undefined;
  }, [orgId, loadFrameworks]);

  const filteredRows = useMemo(() => {
    if (!isReseller || !search) return rows;
    const query = search.toLowerCase();
    return rows.filter((row) => row.name.toLowerCase().includes(query));
  }, [rows, search, isReseller]);

  const pagedRows = useMemo(() => {
    if (!isReseller) return filteredRows;
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, isReseller, page, rowsPerPage]);

  const displayRows = useMemo(
    () =>
      pagedRows.map((row) => ({
        ...row,
        frameworks: drafts[row.id] ?? row.frameworks,
      })),
    [pagedRows, drafts],
  );

  const dirtyEntries = useMemo(
    () =>
      Object.entries(drafts).filter(([clientId, frameworks]) => {
        const saved = baseline[clientId];
        if (!saved) return true;
        return !frameworksEqual(frameworks, saved);
      }),
    [drafts, baseline],
  );
  const isDirty = dirtyEntries.length > 0;

  const handleToggle = useCallback(
    (clientId: string, framework: string, checked: boolean) => {
      setDrafts((prev) => {
        const current =
          prev[clientId] ??
          rows.find((row) => row.id === clientId)?.frameworks ??
          baseline[clientId] ??
          [];
        const next = checked
          ? Array.from(new Set([...current, framework]))
          : current.filter((value) => value !== framework);
        if (frameworksEqual(next, baseline[clientId] ?? [])) {
          const { [clientId]: _removed, ...rest } = prev;
          return rest;
        }
        return { ...prev, [clientId]: next };
      });
    },
    [rows, baseline],
  );

  const handleSave = useCallback(async () => {
    if (!dirtyEntries.length || saving) return;

    setSaving(true);
    try {
      await patchOrganizationFrameworks(
        dirtyEntries.map(([organizationId, frameworks]) => ({
          organizationId,
          frameworks,
        })),
      );

      const savedIds = dirtyEntries.map(([id]) => id);
      setBaseline((prev) => {
        const next = { ...prev };
        dirtyEntries.forEach(([id, frameworks]) => {
          next[id] = [...frameworks];
        });
        return next;
      });
      setDrafts((prev) => {
        const next = { ...prev };
        savedIds.forEach((id) => {
          delete next[id];
        });
        return next;
      });
      setRows((prev) =>
        prev.map((row) => {
          const match = dirtyEntries.find(([id]) => id === row.id);
          return match ? { ...row, frameworks: [...match[1]] } : row;
        }),
      );
      toast.success(t('saveSuccess'));
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error) ?? t('saveFailed'));
    } finally {
      setSaving(false);
    }
  }, [dirtyEntries, saving, t]);

  const columns: Column[] = useMemo(() => {
    const frameworkColumns: Column[] = COMPLIANCE_SETTINGS_FRAMEWORK_OPTIONS.map((option) => ({
      id: `framework-${option.value}`,
      label: option.label,
      align: 'center',
      render: (row) => {
        const item = row as unknown as ComplianceFrameworkOrg;
        const checked = item.frameworks.includes(option.value);
        return (
          <Checkbox
            size="small"
            checked={checked}
            disabled={saving}
            onChange={(event) => {
              event.stopPropagation();
              handleToggle(item.id, option.value, event.target.checked);
            }}
            onClick={(event) => event.stopPropagation()}
          />
        );
      },
    }));

    if (!isReseller) {
      return frameworkColumns;
    }

    return [
      {
        id: 'client',
        label: t('clientColumn'),
        render: (row) => (
          <span style={{ fontWeight: 600, color: EDR_COLORS.textPrimary }}>
            {(row as unknown as ComplianceFrameworkOrg).name || '—'}
          </span>
        ),
      },
      ...frameworkColumns,
    ];
  }, [handleToggle, isReseller, saving, t]);

  const saveButton = isDirty ? (
    <Button
      variant="contained"
      size="small"
      style={{ textTransform: 'none' }}
      loading={saving}
      disabled={saving || loading}
      onClick={() => {
        void handleSave();
      }}
    >
      {t('saveChanges')}
    </Button>
  ) : undefined;

  const toolbar = isReseller ? (
    <EdrDataToolbar
      searchValue={searchInput}
      onSearchChange={setSearchInput}
      searchPlaceholder={t('searchClient')}
      filters={[]}
      trailing={saveButton}
    />
  ) : (
    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" gap={2}>
      <Typography style={{ fontSize: 13, fontWeight: 700, color: EDR_COLORS.textPrimary }}>
        {t('frameworksTitle')}
      </Typography>
      {saveButton}
    </Box>
  );

  const totalRecords = filteredRows.length;

  return (
    <EdrDataCard toolbar={toolbar}>
      <Table
        columns={columns}
        data={displayRows as unknown as TableData[]}
        loading={loading}
        showPagination={isReseller}
        page={page}
        rowsPerPage={rowsPerPage}
        totalRows={totalRecords}
        onPageChange={setPage}
        onRowsPerPageChange={(next) => {
          setRowsPerPage(next);
          setPage(0);
        }}
        TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
      />
      {isReseller ? (
        <Box px={2} py={1.5}>
          <Typography style={{ fontSize: 12, ...edrMutedStyle }}>
            {t('showingRange', {
              from: totalRecords === 0 ? 0 : page * rowsPerPage + 1,
              to: Math.min((page + 1) * rowsPerPage, totalRecords),
              total: formatNumber(totalRecords),
            })}
          </Typography>
        </Box>
      ) : null}
    </EdrDataCard>
  );
};

const ComplianceSettings: FC = () => {
  const { t } = useTranslation('securityComplianceSettings');
  const { userData } = useUserProfile();
  const orgId = userData?.organization?.id ?? '';
  const isReseller = userData?.organization?.organizationTypeName === 'Reseller';

  const breadcrumb = {
    links: [
      { name: 'Security', to: '/mindtime/security' },
      { name: t('title'), to: '/mindtime/security/compliance-settings' },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{getPageTitle(t('pageTitle'))}</title>
      </Helmet>

      <Box
        p={3}
        display="flex"
        flexDirection="column"
        style={{ gap: 16, minHeight: '100%', boxSizing: 'border-box' }}
      >
        <EdrPageHeader
          breadcrumb={breadcrumb}
          title={t('title')}
          subtitle={isReseller ? t('resellerSubtitle') : t('clientSubtitle')}
        />

        {!orgId ? (
          <Typography style={edrMutedStyle}>{t('orgMissing')}</Typography>
        ) : (
          <ComplianceMatrix orgId={orgId} isReseller={isReseller} />
        )}
      </Box>
    </>
  );
};

export default ComplianceSettings;
